import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { BASE_URL } from "../../config";
import { AnimatePresence, motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCirclePlus,
  faPenToSquare,
  faUserShield,
  faUserCheck,
  faUserSlash,
  faRotateRight,
  faXmark,
  faEllipsisVertical,
} from "@fortawesome/free-solid-svg-icons";

const ACTION_MENU_WIDTH = 288;
const ACTION_MENU_HEIGHT = 288;
const ACTION_MENU_MARGIN = 12;
const ACTION_MENU_GAP = 8;

const getActionMenuPosition = (triggerElement) => {
  const rect = triggerElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = rect.right - ACTION_MENU_WIDTH;
  left = Math.max(
    ACTION_MENU_MARGIN,
    Math.min(left, viewportWidth - ACTION_MENU_WIDTH - ACTION_MENU_MARGIN)
  );

  let top = rect.bottom + ACTION_MENU_GAP;
  let placement = "bottom";

  if (top + ACTION_MENU_HEIGHT > viewportHeight - ACTION_MENU_MARGIN) {
    top = rect.top - ACTION_MENU_HEIGHT - ACTION_MENU_GAP;
    placement = "top";
  }

  const maxTop = Math.max(
    ACTION_MENU_MARGIN,
    viewportHeight - ACTION_MENU_HEIGHT - ACTION_MENU_MARGIN
  );
  top = Math.max(ACTION_MENU_MARGIN, Math.min(top, maxTop));

  return { top, left, placement };
};

const SystemUsers = () => {
  const [users, setUsers] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingScopes, setLoadingScopes] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [scopeSearch, setScopeSearch] = useState("");
  const [savingScopes, setSavingScopes] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: "",
    password: "",
    first_name: "",
    last_name: "",
    email: "",
    email_verified: "1",
    active: "1",
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(false);
  const [editForm, setEditForm] = useState({
    username: "",
    password: "",
    first_name: "",
    last_name: "",
    email: "",
    email_verified: "1",
    active: "1",
    v1_user_id: "",
    loa_approval_bypass: "0",
  });
  const [showResetModal, setShowResetModal] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [openMenu, setOpenMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);

  const token = localStorage.getItem("authentication/token");
  const headers = { Authorization: `Bearer ${token}` };

  const closeActionMenu = useCallback(() => {
    setOpenMenu(null);
    setActionMenuPosition(null);
  }, []);

  const handleActionMenuToggle = useCallback(
    (user, event) => {
      event.stopPropagation();

      if (openMenu === user.username) {
        closeActionMenu();
        return;
      }

      setActionMenuPosition(getActionMenuPosition(event.currentTarget));
      setOpenMenu(user.username);
    },
    [closeActionMenu, openMenu]
  );

  useEffect(() => {
    if (!openMenu) {
      return undefined;
    }

    const handleViewportChange = () => closeActionMenu();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [closeActionMenu, openMenu]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        search: searchTerm,
      };
      if (activeFilter !== "") {
        params.active = activeFilter;
      }
      const response = await axios.get(`${BASE_URL}/system-users`, {
        params,
        headers,
      });
      if (response.data.status !== "success") {
        toast.error(response.data.message || "Failed to load users.");
        return;
      }
      setUsers(response.data.data.data || []);
      setTotalCount(response.data.data.total || 0);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to fetch system users."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchScopes = async (forceRefresh = false) => {
    setLoadingScopes(true);
    try {
      const response = await axios.get(`${BASE_URL}/system-users/scopes`, {
        params: forceRefresh ? { refresh: 1 } : undefined,
        headers,
      });
      if (response.data.status !== "success") {
        toast.error(response.data.message || "Failed to load scopes.");
        return;
      }
      setScopes(response.data.data || []);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to fetch scope list."
      );
    } finally {
      setLoadingScopes(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, itemsPerPage, searchTerm, activeFilter]);

  useEffect(() => {
    fetchScopes();
  }, []);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const openScopeModal = (user) => {
    setSelectedUser(user);
    const existingScopes = user.scope_list || splitScopes(user.scope || "");
    setSelectedScopes(existingScopes);
    setScopeSearch("");
  };

  const openCreateModal = () => {
    setCreateForm({
      username: "",
      password: "",
      first_name: "",
      last_name: "",
      email: "",
      email_verified: "1",
      active: "1",
    });
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    if (creatingUser) return;
    setShowCreateModal(false);
  };

  const openEditModal = (user) => {
    setEditForm({
      username: user.username,
      password: "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      email_verified: String(user.email_verified ? 1 : 0),
      active: String(user.active ? 1 : 0),
      v1_user_id: user.v1_user_id != null ? String(user.v1_user_id) : "",
      loa_approval_bypass: String(user.loa_approval_bypass ? 1 : 0),
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    if (editingUser) return;
    setShowEditModal(false);
  };

  const openResetModal = (user) => {
    setResetTarget(user);
    setResetPassword("");
    setShowResetModal(true);
  };

  const closeResetModal = () => {
    if (resettingPassword) return;
    setShowResetModal(false);
    setResetTarget(null);
  };

  const closeScopeModal = () => {
    if (savingScopes) return;
    setSelectedUser(null);
    setSelectedScopes([]);
    setScopeSearch("");
  };

  const splitScopes = (scopeString) => {
    if (!scopeString) return [];
    return scopeString
      .split(/\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const groupedScopes = useMemo(() => {
    const filtered = scopes.filter((scope) =>
      scope.scope.toLowerCase().includes(scopeSearch.toLowerCase())
    );
    return filtered.reduce((acc, item) => {
      const group = item.scope.includes(":")
        ? item.scope.split(":")[0]
        : "general";
      if (!acc[group]) acc[group] = [];
      acc[group].push(item);
      return acc;
    }, {});
  }, [scopes, scopeSearch]);

  const toggleScope = (scopeValue) => {
    setSelectedScopes((prev) => {
      if (prev.includes(scopeValue)) {
        return prev.filter((s) => s !== scopeValue);
      }
      return [...prev, scopeValue];
    });
  };

  const selectAllFiltered = () => {
    const filteredScopes = scopes
      .filter((scope) =>
        scope.scope.toLowerCase().includes(scopeSearch.toLowerCase())
      )
      .map((scope) => scope.scope);
    setSelectedScopes((prev) => Array.from(new Set([...prev, ...filteredScopes])));
  };

  const clearFiltered = () => {
    const filteredScopes = scopes
      .filter((scope) =>
        scope.scope.toLowerCase().includes(scopeSearch.toLowerCase())
      )
      .map((scope) => scope.scope);
    setSelectedScopes((prev) => prev.filter((s) => !filteredScopes.includes(s)));
  };

  const saveScopes = async () => {
    if (!selectedUser) return;
    setSavingScopes(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/system-users/${selectedUser.username}/scopes`,
        { scopes: selectedScopes },
        { headers }
      );
      if (response.data.status !== "success") {
        toast.error(response.data.message || "Failed to update scopes.");
        return;
      }
      toast.success("User scopes updated.");
      closeScopeModal();
      fetchUsers();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update user scopes."
      );
    } finally {
      setSavingScopes(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    try {
      const payload = {
        username: createForm.username.trim(),
        password: createForm.password,
        first_name: createForm.first_name.trim(),
        last_name: createForm.last_name.trim(),
        email: createForm.email.trim(),
        email_verified: Number(createForm.email_verified),
        active: Number(createForm.active),
      };
      const response = await axios.post(
        `${BASE_URL}/system-users`,
        payload,
        { headers }
      );
      if (response.data.status !== "success") {
        toast.error(response.data.message || "Failed to create user.");
        return;
      }
      toast.success("User created. Add scopes next.");
      setShowCreateModal(false);
      fetchUsers();
      if (response.data.data) {
        openScopeModal(response.data.data);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to create system user."
      );
    } finally {
      setCreatingUser(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setEditingUser(true);
    try {
      const payload = {
        password: editForm.password || undefined,
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
        email: editForm.email.trim(),
        email_verified: Number(editForm.email_verified),
        active: Number(editForm.active),
        v1_user_id: editForm.v1_user_id !== "" ? Number(editForm.v1_user_id) : null,
        loa_approval_bypass: Number(editForm.loa_approval_bypass),
      };
      const response = await axios.put(
        `${BASE_URL}/system-users/${editForm.username}`,
        payload,
        { headers }
      );
      if (response.data.status !== "success") {
        toast.error(response.data.message || "Failed to update user.");
        return;
      }
      toast.success("User updated.");
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update system user."
      );
    } finally {
      setEditingUser(false);
    }
  };

  const generateRandomPassword = () => {
    const chars =
      "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%*?";
    let result = "";
    for (let i = 0; i < 12; i += 1) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setResetPassword(result);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetTarget) return;
    setResettingPassword(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/system-users/${resetTarget.username}/reset-password`,
        { password: resetPassword },
        { headers }
      );
      if (response.data.status !== "success") {
        toast.error(response.data.message || "Failed to reset password.");
        return;
      }
      toast.success("Password reset successfully.");
      setShowResetModal(false);
      setResetTarget(null);
      setResetPassword("");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to reset password."
      );
    } finally {
      setResettingPassword(false);
    }
  };

  const toggleUserStatus = async (user) => {
    const nextActive = user.active === 1 ? 0 : 1;
    try {
      const response = await axios.put(
        `${BASE_URL}/system-users/${user.username}/status`,
        { active: nextActive },
        { headers }
      );
      if (response.data.status !== "success") {
        toast.error(response.data.message || "Failed to update status.");
        return;
      }
      toast.success("User status updated.");
      fetchUsers();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update user status."
      );
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
        <div className="text-sm text-gray-600">
          Showing{" "}
          <span className="font-semibold text-gray-900">
            {(currentPage - 1) * itemsPerPage + 1}
          </span>{" "}
          to{" "}
          <span className="font-semibold text-gray-900">
            {Math.min(currentPage * itemsPerPage, totalCount)}
          </span>{" "}
          of <span className="font-semibold text-gray-900">{totalCount}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="px-3 py-1.5 rounded-md border border-gray-200 text-sm disabled:opacity-40"
          >
            First
          </button>
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 rounded-md border border-gray-200 text-sm disabled:opacity-40"
          >
            Prev
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 rounded-md border border-gray-200 text-sm disabled:opacity-40"
          >
            Next
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 rounded-md border border-gray-200 text-sm disabled:opacity-40"
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-gray-200 bg-white px-6 py-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              System Users Management
            </h1>
            <p className="text-sm text-gray-600">
              Manage user profiles, access scopes, and status.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.03 }}
              onClick={openCreateModal}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              <FontAwesomeIcon icon={faCirclePlus} className="mr-2" />
              Add User
            </motion.button>
            <select
              value={activeFilter}
              onChange={(e) => {
                setCurrentPage(1);
                setActiveFilter(e.target.value);
              }}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setCurrentPage(1);
                setItemsPerPage(Number(e.target.value));
              }}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
            >
              {[10, 20, 50, 100].map((count) => (
                <option key={count} value={count}>
                  Show {count}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <input
            value={searchTerm}
            onChange={(e) => {
              setCurrentPage(1);
              setSearchTerm(e.target.value);
            }}
            placeholder="Search by username, name, or email..."
            className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          />
        </div>
      </div>

      <div className="p-6">
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left">ID</th>
                  <th className="px-4 py-3 text-left">Username</th>
                  <th className="px-4 py-3 text-left">First Name</th>
                  <th className="px-4 py-3 text-left">Last Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Email Verified</th>
                  <th className="px-4 py-3 text-left">Active</th>
                  <th className="px-4 py-3 text-left">LOA Bypass</th>
                  <th className="px-4 py-3 text-left">Scopes</th>
                  <th className="px-4 py-3 text-left">Created</th>
                  <th className="px-4 py-3 text-left">Updated</th>
                  <th className="px-4 py-3 text-left">V1 User ID</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td className="px-4 py-6 text-center" colSpan={13}>
                      Loading users...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-center" colSpan={13}>
                      No system users found.
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.username} className="hover:bg-slate-50">
                      <td className="px-4 py-3">{user.id}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">
                        {user.username}
                      </td>
                      <td className="px-4 py-3">{user.first_name || "-"}</td>
                      <td className="px-4 py-3">{user.last_name || "-"}</td>
                      <td className="px-4 py-3">{user.email || "-"}</td>
                      <td className="px-4 py-3">
                        {user.email_verified ? "Yes" : "No"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                            user.active
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-rose-100 text-rose-700"
                          }`}
                        >
                          {user.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                            user.loa_approval_bypass
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {user.loa_approval_bypass ? "Enabled" : "Off"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(user.scope_list || splitScopes(user.scope || ""))
                            .slice(0, 3)
                            .map((scope) => (
                              <span
                                key={scope}
                                className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700"
                              >
                                {scope}
                              </span>
                            ))}
                          {(user.scope_list || splitScopes(user.scope || "")).length >
                            3 && (
                            <span className="text-xs text-gray-500">
                              +{(user.scope_list || splitScopes(user.scope || "")).length - 3} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {user.created_at
                          ? new Date(user.created_at).toLocaleString()
                          : "-"}
                      </td>
                      <td className="px-4 py-3">
                        {user.updated_at
                          ? new Date(user.updated_at).toLocaleString()
                          : "-"}
                      </td>
                      <td className="px-4 py-3">{user.v1_user_id || "-"}</td>
                      <td className="px-4 py-3">
                        <div className="relative">
                          <button
                            type="button"
                            aria-label={`Actions for system user ${user.username}`}
                            onClick={(event) => handleActionMenuToggle(user, event)}
                            className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold shadow-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 ${
                              openMenu === user.username
                                ? "border-indigo-400 bg-indigo-50 text-indigo-700 shadow-indigo-100"
                                : "border-gray-300 bg-white text-gray-700 hover:-translate-y-0.5 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
                            }`}
                          >
                            <FontAwesomeIcon
                              icon={faEllipsisVertical}
                              className="text-gray-500"
                            />
                            <span>Actions</span>
                          </button>
                          {openMenu === user.username &&
                            actionMenuPosition &&
                            typeof document !== "undefined" &&
                            createPortal(
                              <>
                                <button
                                  type="button"
                                  aria-label="Close system user actions menu"
                                  className="fixed inset-0 z-[9998] cursor-default bg-transparent"
                                  onClick={closeActionMenu}
                                />
                                <div
                                  className="fixed z-[9999] w-72 overflow-visible"
                                  style={{
                                    top: `${actionMenuPosition.top}px`,
                                    left: `${actionMenuPosition.left}px`,
                                  }}
                                >
                                  <div
                                    className={`absolute right-6 h-3 w-3 rotate-45 border bg-white ${
                                      actionMenuPosition.placement === "top"
                                        ? "-bottom-1.5 border-l-0 border-t-0 border-gray-200"
                                        : "-top-1.5 border-b-0 border-r-0 border-gray-200"
                                    }`}
                                  />
                                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl shadow-slate-900/15 ring-1 ring-black/5">
                                    <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white px-4 py-3">
                                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        User Actions
                                      </p>
                                      <p className="truncate text-sm font-semibold text-slate-900">
                                        {user.username}
                                      </p>
                                    </div>
                                    <div className="space-y-1 p-2">
                                      {[
                                        {
                                          key: "scopes",
                                          label: "Manage Scopes",
                                          icon: faUserShield,
                                          color: "text-indigo-600",
                                          onClick: () => openScopeModal(user),
                                        },
                                        {
                                          key: "edit",
                                          label: "Edit User",
                                          icon: faPenToSquare,
                                          color: "text-slate-600",
                                          onClick: () => openEditModal(user),
                                        },
                                        {
                                          key: "reset",
                                          label: "Reset Password",
                                          icon: faRotateRight,
                                          color: "text-amber-600",
                                          onClick: () => openResetModal(user),
                                        },
                                        {
                                          key: "status",
                                          label: user.active ? "Deactivate" : "Activate",
                                          icon: user.active ? faUserSlash : faUserCheck,
                                          color: user.active
                                            ? "text-rose-600"
                                            : "text-emerald-600",
                                          onClick: () => toggleUserStatus(user),
                                        },
                                      ].map((action) => (
                                        <button
                                          key={action.key}
                                          type="button"
                                          onClick={() => {
                                            action.onClick();
                                            closeActionMenu();
                                          }}
                                          className="group flex w-full items-center justify-between rounded-lg border border-transparent px-3 py-2.5 text-left text-sm text-gray-700 transition-all duration-150 hover:translate-x-0.5 hover:border-indigo-100 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-sky-50 hover:text-indigo-700 hover:shadow-sm"
                                        >
                                          <span className="flex min-w-0 items-center">
                                            <span className="mr-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 transition-all duration-150 group-hover:bg-white group-hover:shadow-sm">
                                              <FontAwesomeIcon
                                                icon={action.icon}
                                                className={`text-sm ${action.color}`}
                                              />
                                            </span>
                                            <span className="truncate font-medium">
                                              {action.label}
                                            </span>
                                          </span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </>,
                              document.body
                            )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </div>
      </div>

      <AnimatePresence>
        {selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            >
            <div className="border-b border-gray-200 px-6 py-4">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Manage Scopes
                  </h2>
                  <p className="text-sm text-gray-600">
                    Update scopes for {selectedUser.username}
                  </p>
                </div>
                <button
                  onClick={closeScopeModal}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                >
                  <FontAwesomeIcon icon={faXmark} className="mr-2" />
                  Close
                </button>
              </div>
            </div>

            <div className="grid max-h-[70vh] grid-cols-1 gap-6 overflow-y-auto px-6 py-5 md:grid-cols-3">
              <div className="md:col-span-1">
                <label className="text-xs font-semibold uppercase text-gray-500">
                  Search scopes
                </label>
                <input
                  value={scopeSearch}
                  onChange={(e) => setScopeSearch(e.target.value)}
                  placeholder="Type to filter scopes..."
                  className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                />
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={selectAllFiltered}
                  className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700"
                >
                  Select filtered
                </button>
                <button
                  onClick={clearFiltered}
                  className="rounded-md border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700"
                >
                  Clear filtered
                </button>
                <button
                  onClick={() => fetchScopes(true)}
                  className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700"
                >
                  <FontAwesomeIcon icon={faRotateRight} className="mr-2" />
                  Refresh scopes
                </button>
              </div>
                <div className="mt-6">
                  <div className="text-xs font-semibold uppercase text-gray-500">
                    Selected scopes ({selectedScopes.length})
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedScopes.length === 0 ? (
                      <span className="text-sm text-gray-500">None</span>
                    ) : (
                      selectedScopes.map((scope) => (
                        <span
                          key={scope}
                          className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-800"
                        >
                          {scope}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                {loadingScopes ? (
                  <div className="text-sm text-gray-500">Loading scopes...</div>
                ) : (
                  Object.keys(groupedScopes).map((group) => (
                    <div key={group} className="mb-6">
                      <div className="mb-2 text-xs font-semibold uppercase text-gray-500">
                        {group}
                      </div>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {groupedScopes[group].map((scope) => (
                          <label
                            key={scope.scope}
                            className="flex items-start gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm hover:border-indigo-300"
                          >
                            <input
                              type="checkbox"
                              checked={selectedScopes.includes(scope.scope)}
                              onChange={() => toggleScope(scope.scope)}
                              className="mt-1"
                            />
                            <div>
                              <div className="font-medium text-gray-900">
                                {scope.scope}
                              </div>
                              <div className="text-xs text-gray-500">
                                Default: {scope.is_default ? "Yes" : "No"}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
              <div className="text-sm text-gray-500">
                Total scopes available: {scopes.length}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={closeScopeModal}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm"
                  disabled={savingScopes}
                >
                  Cancel
                </button>
                <button
                  onClick={saveScopes}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                  disabled={savingScopes}
                >
                  {savingScopes ? "Saving..." : "Save Scopes"}
                </button>
              </div>
            </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl"
            >
            <div className="border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Add New System User
                  </h2>
                  <p className="text-sm text-gray-600">
                    Create an account, then assign scopes.
                  </p>
                </div>
                <button
                  onClick={closeCreateModal}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                >
                  <FontAwesomeIcon icon={faXmark} className="mr-2" />
                  Close
                </button>
              </div>
            </div>
            <form onSubmit={handleCreateUser} className="px-6 py-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-500">
                    Username
                  </label>
                  <input
                    required
                    value={createForm.username}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        username: e.target.value,
                      }))
                    }
                    className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-500">
                    Password
                  </label>
                  <input
                    type="password"
                    required
                    value={createForm.password}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-500">
                    First Name
                  </label>
                  <input
                    required
                    value={createForm.first_name}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        first_name: e.target.value,
                      }))
                    }
                    className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-500">
                    Last Name
                  </label>
                  <input
                    required
                    value={createForm.last_name}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        last_name: e.target.value,
                      }))
                    }
                    className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold uppercase text-gray-500">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={createForm.email}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-500">
                    Email Verified
                  </label>
                  <select
                    value={createForm.email_verified}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        email_verified: e.target.value,
                      }))
                    }
                    className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  >
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-500">
                    Active
                  </label>
                  <select
                    value={createForm.active}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        active: e.target.value,
                      }))
                    }
                    className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm"
                  disabled={creatingUser}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                  disabled={creatingUser}
                >
                  {creatingUser ? "Creating..." : "Create User"}
                </button>
              </div>
            </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl"
            >
              <div className="border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Edit System User
                    </h2>
                    <p className="text-sm text-gray-600">
                      Update user profile details.
                    </p>
                  </div>
                  <button
                    onClick={closeEditModal}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                  >
                    <FontAwesomeIcon icon={faXmark} className="mr-2" />
                    Close
                  </button>
                </div>
              </div>
              <form onSubmit={handleEditUser} className="px-6 py-5">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">
                      Username (readonly)
                    </label>
                    <input
                      value={editForm.username}
                      readOnly
                      className="mt-2 w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">
                      New Password (optional)
                    </label>
                    <input
                      type="password"
                      value={editForm.password}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }))
                      }
                      className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">
                      First Name
                    </label>
                    <input
                      required
                      value={editForm.first_name}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          first_name: e.target.value,
                        }))
                      }
                      className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">
                      Last Name
                    </label>
                    <input
                      required
                      value={editForm.last_name}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          last_name: e.target.value,
                        }))
                      }
                      className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-semibold uppercase text-gray-500">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={editForm.email}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">
                      Email Verified
                    </label>
                    <select
                      value={editForm.email_verified}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          email_verified: e.target.value,
                        }))
                      }
                      className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    >
                      <option value="1">Yes</option>
                      <option value="0">No</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">
                      Active
                    </label>
                    <select
                      value={editForm.active}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          active: e.target.value,
                        }))
                      }
                      className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    >
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">
                      V1 User ID
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={editForm.v1_user_id}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          v1_user_id: e.target.value,
                        }))
                      }
                      placeholder="(optional)"
                      className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <label className="text-sm font-semibold text-emerald-900">
                            LOA Approval Bypass
                          </label>
                          <p className="mt-1 text-xs leading-5 text-emerald-800">
                            Enable this if the OAuth user can generate LOA transactions
                            for all coverages without pre-audit approval. Existing
                            pending approval records are not changed.
                          </p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={editForm.loa_approval_bypass === "1"}
                          onClick={() =>
                            setEditForm((prev) => ({
                              ...prev,
                              loa_approval_bypass:
                                prev.loa_approval_bypass === "1" ? "0" : "1",
                            }))
                          }
                          className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition ${
                            editForm.loa_approval_bypass === "1"
                              ? "bg-emerald-600"
                              : "bg-gray-300"
                          }`}
                        >
                          <span
                            className={`inline-block h-6 w-6 rounded-full bg-white shadow transition ${
                              editForm.loa_approval_bypass === "1"
                                ? "translate-x-7"
                                : "translate-x-1"
                            }`}
                          />
                          <span className="sr-only">Toggle LOA approval bypass</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="rounded-lg border border-gray-200 px-4 py-2 text-sm"
                    disabled={editingUser}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                    disabled={editingUser}
                  >
                    {editingUser ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showResetModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="w-full max-w-xl rounded-2xl bg-white shadow-2xl"
            >
              <div className="border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Reset User Password
                    </h2>
                    <p className="text-sm text-gray-600">
                      Set a new password for {resetTarget?.username || "user"}.
                    </p>
                  </div>
                  <button
                    onClick={closeResetModal}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                  >
                    <FontAwesomeIcon icon={faXmark} className="mr-2" />
                    Close
                  </button>
                </div>
              </div>
              <form onSubmit={handleResetPassword} className="px-6 py-5">
                <label className="text-xs font-semibold uppercase text-gray-500">
                  New Password
                </label>
                <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-center">
                  <input
                    type="text"
                    required
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={generateRandomPassword}
                    className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700"
                  >
                    Generate
                  </button>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeResetModal}
                    className="rounded-lg border border-gray-200 px-4 py-2 text-sm"
                    disabled={resettingPassword}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
                    disabled={resettingPassword}
                  >
                    {resettingPassword ? "Saving..." : "Reset Password"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SystemUsers;
